// @api/auth.js
const API_URL = import.meta.env.VITE_API_URL

const EMPTY_SESSION = {token: null, expiresAt: null}

const getStoredSession = () => {
  if (typeof window === 'undefined') {
    return {...EMPTY_SESSION}
  }

  try {
    const raw = localStorage.getItem('auth')
    if (!raw) {
      return {...EMPTY_SESSION}
    }

    const parsed = JSON.parse(raw)
    const normalizedExpiresAt =
      typeof parsed?.expiresAt === 'number' ? parsed.expiresAt : Number(parsed?.expiresAt)

    return {
      token: typeof parsed?.token === 'string' ? parsed.token : null,
      expiresAt: Number.isFinite(normalizedExpiresAt) ? normalizedExpiresAt : null,
    }
  } catch {
    return {...EMPTY_SESSION}
  }
}

const getActiveAuthorizationHeader = ({allowExpiredWithinMs = 0} = {}) => {
  const {token, expiresAt} = getStoredSession()
  const effectiveExpiry = Number.isFinite(expiresAt) ? expiresAt + allowExpiredWithinMs : null
  if (!token || !Number.isFinite(effectiveExpiry) || effectiveExpiry <= Date.now()) {
    return null
  }

  return `Bearer ${token}`
}

const extractMessage = async (res, fallbackMessage) => {
  try {
    const errorBody = await res.clone().json()
    const extracted = [
      errorBody?.error?.message,
      errorBody?.error,
      errorBody?.message,
      errorBody?.details,
      errorBody?.msg,
    ].find((value) => typeof value === 'string' && value.trim())

    if (extracted) {
      return extracted.trim()
    }
  } catch {
    // ignore JSON parsing errors and fall back to text response
  }

  try {
    const text = await res.text()
    if (text?.trim()) {
      return text.trim()
    }
  } catch {
    // ignore text parsing errors and fall back to provided fallback
  }

  return fallbackMessage
}

const normalizeClientPayload = (client) => {
  if (!client || typeof client !== 'object') {
    return null
  }

  const ip = typeof client.ip === 'string' ? client.ip.trim() : ''
  const userAgent = typeof client.userAgent === 'string' ? client.userAgent.trim() : ''

  const payload = {}

  if (ip) {
    payload.ip = ip
  }

  if (userAgent) {
    payload.userAgent = userAgent
  }

  return Object.keys(payload).length ? payload : null
}

const normalizeEmailInput = (email) =>
  typeof email === 'string' ? email.trim().toLowerCase() : email
// Normalize auth emails client-side for UX consistency across login/register/
// reset flows. Backend remains the source of truth and also normalizes lookup.

const authHeaders = (includeJson = true) => {
  const authorization = getActiveAuthorizationHeader()
  const headers = {
    'Accept': 'application/json',
  }
  if (includeJson) {
    headers['Content-Type'] = 'application/json'
  }
  if (authorization) {
    headers['Authorization'] = authorization
  }
  return headers
}

export async function fetchClientInfo() {
  const res = await fetch(`${API_URL}/auth/client-info`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Failed to resolve client info'))
  }

  const data = await res.json()

  return {
    ip: typeof data?.ip === 'string' && data.ip.trim() ? data.ip.trim() : null,
    userAgent: typeof data?.userAgent === 'string' && data.userAgent.trim() ? data.userAgent.trim() : null,
  }
}

export async function loginUser({ email, password, twoFactorMethod, client } = {}) {
  const payload = { email: normalizeEmailInput(email), password, twoFactorMethod }
  const normalizedClient = normalizeClientPayload(client)
  if (normalizedClient) {
    payload.client = normalizedClient
  }

  const res = await fetch(`${API_URL}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Login failed'))
  }

  const data = await res.json()

  if (data?.requires2FA) {
    if (!data.sessionId || !Array.isArray(data.methods)) {
      throw new Error('Invalid two-factor response from server')
    }
    return data
  }

  if (!data?.token || !data?.user) {
    throw new Error('Invalid response from server')
  }

  return data
}

export async function registerUser({
  name = '',
  email,
  password,
  language,
  inviteCode,
  acceptedTermsVersion,
  acceptedPrivacyVersion,
}) {
  const res = await fetch(`${API_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      email: normalizeEmailInput(email),
      password,
      language,
      inviteCode,
      acceptedTermsVersion,
      acceptedPrivacyVersion,
    })
  })

  if (!res.ok) {
    const msg = await res.text()
    throw new Error(msg || 'Registration failed')
  }

  return await res.json()
}

export async function requestPasswordReset({ email }) {
  const origin = window.location.origin
  const res = await fetch(`${API_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normalizeEmailInput(email), origin })
  })

  if (!res.ok) {
    const msg = await res.text()
    throw new Error(msg || 'Request failed')
  }

  return await res.json()
}

export async function resetPassword({ email, token, password }) {
  const res = await fetch(`${API_URL}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normalizeEmailInput(email), token, password })
  })

  if (!res.ok) {
    const msg = await res.text()
    throw new Error(msg || 'Request failed')
  }

  return await res.json()
}

export async function resendVerification({ email }) {
  const res = await fetch(`${API_URL}/auth/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: normalizeEmailInput(email) })
  })

  if (!res.ok) {
    const msg = await res.text()
    throw new Error(msg || 'Request failed')
  }

  return await res.json()
}

export async function requestEmailOtp({ sessionId, client } = {}) {
  const payload = { sessionId }
  const normalizedClient = normalizeClientPayload(client)
  if (normalizedClient) {
    payload.client = normalizedClient
  }

  const res = await fetch(`${API_URL}/auth/login/send-email-otp`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Failed to send verification code'))
  }

  return res.json()
}

export async function completeTwoFactor({ sessionId, method, code, client } = {}) {
  const payload = { sessionId, method, code }
  const normalizedClient = normalizeClientPayload(client)
  if (normalizedClient) {
    payload.client = normalizedClient
  }

  const res = await fetch(`${API_URL}/auth/login/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Verification failed'))
  }

  return res.json()
}

export const verifyEmailOtp = ({ sessionId, code, client }) =>
  completeTwoFactor({ sessionId, method: 'email', code, client })

export async function logout({reason = 'manual', client} = {}) {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }

  const authorization = getActiveAuthorizationHeader({allowExpiredWithinMs: 60_000})
  if (authorization) {
    headers['Authorization'] = authorization
  }

  const payload = {reason}
  const normalizedClient = normalizeClientPayload(client)
  if (normalizedClient) {
    payload.client = normalizedClient
  }

  try {
    const res = await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })

    if (!res.ok) {
      const message = await extractMessage(res, 'Logout failed')
      console.warn(`[auth] Logout request failed (${res.status})`, message)
      return false
    }

    return true
  } catch (error) {
    console.warn('[auth] Logout request error', error)
    return false
  }
}

export async function sendPresenceHeartbeat({path, source} = {}) {
  const authorization = getActiveAuthorizationHeader()
  if (!authorization) {
    return null
  }

  const res = await fetch(`${API_URL}/v2/me/presence/heartbeat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': authorization,
    },
    body: JSON.stringify({path, source}),
    keepalive: true,
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Failed to update presence heartbeat'))
  }

  return res.json()
}

export async function generateWebAuthnChallenge({ sessionId }) {
  const res = await fetch(`${API_URL}/auth/login/webauthn/challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ sessionId }),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Failed to generate WebAuthn challenge'))
  }

  return res.json()
}

export async function verifyWebAuthnAssertion({ sessionId, assertionResponse }) {
  const res = await fetch(`${API_URL}/auth/login/webauthn/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ sessionId, assertionResponse }),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Failed to verify WebAuthn response'))
  }

  return res.json()
}

export async function generateSessionWebAuthnRegistrationChallenge({ sessionId }) {
  const res = await fetch(`${API_URL}/auth/login/webauthn/register/challenge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ sessionId }),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Unable to start passkey registration'))
  }

  return res.json()
}

export async function verifySessionWebAuthnRegistration({ sessionId, attestationResponse }) {
  const res = await fetch(`${API_URL}/auth/login/webauthn/register/verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({ sessionId, attestationResponse }),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Unable to verify passkey registration'))
  }

  return res.json()
}

export async function listWebAuthnCredentials() {
  const res = await fetch(`${API_URL}/auth/2fa/webauthn/credentials`, {
    method: 'GET',
    headers: authHeaders(false),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Unable to load passkeys'))
  }

  return res.json()
}

export async function updateWebAuthnCredentialLabel({credentialId, label}) {
  const res = await fetch(`${API_URL}/auth/2fa/webauthn/credentials/${encodeURIComponent(credentialId)}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({label}),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Unable to rename passkey'))
  }

  return res.json()
}

export async function deleteWebAuthnCredential({credentialId}) {
  const res = await fetch(`${API_URL}/auth/2fa/webauthn/credentials/${encodeURIComponent(credentialId)}`, {
    method: 'DELETE',
    headers: authHeaders(false),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Unable to remove passkey'))
  }

  return res.json()
}

export async function generateAuthenticatedWebAuthnRegistrationChallenge() {
  const res = await fetch(`${API_URL}/auth/2fa/webauthn/register/challenge`, {
    method: 'GET',
    headers: authHeaders(false),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Unable to start passkey registration'))
  }

  return res.json()
}

export async function verifyAuthenticatedWebAuthnRegistration({attestationResponse}) {
  const res = await fetch(`${API_URL}/auth/2fa/webauthn/register/verify`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({attestationResponse}),
  })

  if (!res.ok) {
    throw new Error(await extractMessage(res, 'Unable to verify passkey registration'))
  }

  return res.json()
}

export function checkAuth() {
  const raw = localStorage.getItem('auth')
  if (!raw) return false

  try {
    const parsed = JSON.parse(raw)
    return !!parsed.token && parsed.expiresAt > Date.now()
  } catch {
    return false
  }
}
